import { HOST_STATUS, MOMENT_TYPES } from '../hostStateSlice';
import { GAMESTATE_ROUND, GAMESTATE_ROUND_FINAL, GAMESTATE_ROUNDSTATE_QUESTION_READING } from '../../gamestate/constants';
import logging from 'utils/logging';
import { advanceState } from '../../gamestate/GameState';
import { registerCacheReset } from '../momentCacheManager';

const log = logging.getLogger('moments.ReadQuestion');
log.setLevel('info');

// Track which questions have been read to avoid duplicate triggers
const questionsRead = new Set();

export const resetReadQuestionCache = () => {
  questionsRead.clear();
};
registerCacheReset('round', resetReadQuestionCache);

export const ReadQuestionMoment = {
  id: 'READ_QUESTION',
  /**
   * Detects when a question is being read
   * @param {Object} param0 - The parameters for the moment
   * @param {Object} param0.prevState - Previous game state
   * @param {Object} param0.currentState - Current game state
   * @param {Array} param0.currentPlayers - Current players array
   * @param {Array} param0.prevPlayers - Previous players array
   * @returns {string|null} - Question ID if should trigger, null otherwise
   */
  shouldTrigger: ({ prevState, currentState, currentPlayers, questions }) => {
    // Only trigger in round state
    if (currentState?.currentState !== GAMESTATE_ROUND) {
      return false;
    }

    // Check if we've transitioned to question reading state
    if (currentState?.roundState !== GAMESTATE_ROUNDSTATE_QUESTION_READING) {
      return false;
    }

    // Skip for final round — FinalQuestionRead moment handles it
    if (currentState?.round === GAMESTATE_ROUND_FINAL) {
      return false;
    }

    // Check if we have a question ID
    const questionId = currentState?.questionId;
    if (!questionId) {
      log.debug('No question ID found in current state');
      return false;
    }

    // Check if this question has already been read
    if (questionsRead.has(questionId)) {
      log.debug(`Question ${questionId} has already been read`);
      return false;
    }

    // Check if we have players
    if (!currentPlayers || currentPlayers.length === 0) {
      log.debug('No players available for question reading');
      return false;
    }

    // Check if we have a player in control
    const playerInControl = currentState?.playerInControl;
    if (!playerInControl) {
      log.debug('No player in control for question reading');
      return false;
    }

    // Check if the controlling player exists
    const controllingPlayer = currentPlayers.find(p => p.id === playerInControl);
    if (!controllingPlayer) {
      log.debug(`Controlling player ${playerInControl} not found in current players`);
      return false;
    }

    // Mark this question as read to prevent duplicate triggers
    questionsRead.add(questionId);
    
    log.debug(`Question reading triggered for question ${questionId} by player ${controllingPlayer.name}`);
    
    
    if (!questionId) {
      return false;
    }

    // Find the question object
    const question = questions?.find(q => q.id === questionId);
    if (!question) {
      log.debug(`Question ${questionId} not found in questions array`);
      return false;
    }

    // Find the controlling player
    const playerId = currentState?.playerInControl;
    const player = currentPlayers?.find(p => p.id === playerId);
    if (!player) {
      log.debug(`Controlling player ${playerId} not found in players array`);
      return false;
    }

    const narrationKey = `READ_QUESTION_${questionId}`;

    return {
      shouldTriggerAction: false,
      shouldGenerateNarration: true,
      narrationParams: {
        narrationKey,
        momentType: MOMENT_TYPES.READ_QUESTION,
        context: {
          questionId,
          questionText: question.clue,
          playerName: player.name,
          playerId: player.id,
          round: currentState.round,
          question: question, // Full question object for additional context
        }
      }
    };
  }
};

export const ReadQuestionPlaybackMoment = {
  id: 'READ_QUESTION_PLAYBACK',

  momentType: MOMENT_TYPES.READ_QUESTION,

  /**
   * Determines when this moment should play
   * @param {Object} gameState - The current game state
   * @param {Object} hostState - The current host state
   * @param {Object} narration - The narration object
   * @param {Set} playedNarrations - Set of narration keys that have been played
   * @returns {boolean} - True if the moment should play
   */
  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    // Check if narration exists
    log.debug("Checking if READ_QUESTION_PLAYBACK should play", gameState, hostState, narration, playedNarrations);
    if (!narration) {
      return false;
    }

    // Check if this is a question reading narration
    if (!narration.momentType || narration.momentType !== MOMENT_TYPES.READ_QUESTION) {
      return false;
    }

    // Check if narration is ready to play
    const narrationReady = narration.textStatus === "READY" && (narration.audioStatus === "READY" || narration.audioStatus === "ERROR");
    if (!narrationReady) {
      return false;
    }

    // Check if we're in the question reading state
    const inQuestionReadingState = gameState?.currentState === GAMESTATE_ROUND && 
                                  gameState?.roundState === GAMESTATE_ROUNDSTATE_QUESTION_READING;
    if (!inQuestionReadingState) {
      return false;
    }

    // Final has its own two-part clue narration. This also prevents stale
    // regular READ_QUESTION narrations from replaying during the final timer.
    if (gameState?.round === GAMESTATE_ROUND_FINAL) {
      return false;
    }

    // Check if the question ID matches
    const questionId = gameState?.questionId;
    const narrationQuestionId = narration?.context?.questionId;
    if (questionId !== narrationQuestionId) {
      return false;
    }

    // Check if host is available to speak (not already speaking)
    const hostAvailable = hostState?.status !== HOST_STATUS.SPEAKING;
    if (!hostAvailable) {
      return false;
    }

    // Check if this specific narration has been played before
    const notPlayedYet = !playedNarrations.has(narration.key);
    if (!notPlayedYet) {
      return false;
    }

    log.debug(`READ_QUESTION_PLAYBACK shouldPlay: narrationReady=${narrationReady}, inQuestionReadingState=${inQuestionReadingState}, hostAvailable=${hostAvailable}, notPlayedYet=${notPlayedYet}`);
    
    return true;
  },

  /**
   * Callback when the narration is complete
   * Advances the game state to the answering phase
   * @param {string} gameId - The game ID
   * @param {function} dispatch - Redux dispatch function
   * @param {function} getState - Redux getState function
   */
  onComplete: async (gameId, dispatch, getState) => {
    try {
      const state = getState();
      const gameObject = state.game?.game;

      if (!gameObject) {
        log.error('Game object not found in state');
        return;
      }

      // Idempotent guard: only advance if state is still in QUESTION_READING.
      // If something else (host click, etc.) already moved past reading,
      // calling advanceState here would silently over-advance — e.g. reading
      // is now 'answering', so getNextState returns 'judging', which fires
      // JudgingComplete with zero submissions (vacuously "all judged"),
      // advances to 'results', and the commit drops in-flight answers.
      const currentRoundState = gameObject.state?.roundState;
      if (currentRoundState !== GAMESTATE_ROUNDSTATE_QUESTION_READING) {
        log.warn(`ReadQuestion.onComplete skipping advance — state already past reading (${currentRoundState})`);
        return;
      }

      log.info('Question reading narration complete, advancing game state to answering phase');
      const players = Object.values(state.player?.entities || {});
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error advancing game state after question reading:', error);
    }
  }
};

const api = {
  trigger: ReadQuestionMoment,
  playback: ReadQuestionPlaybackMoment
};

export default api;
